import {
  Box, SvgIcon, Theme, Typography, Collapse, List, ListItemButton, ListItemText,
  Link,
  useTheme,
} from '@mui/material';
import {
  common,
} from '@mui/material/colors';
import { useLocation, useNavigate } from 'react-router-dom';
import { SxProps } from '@mui/system';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import React, { useEffect, useState } from 'react';

export interface DashboardSubMenuItemsProps {
  title: string;
  path: string;
  isCurrent?: boolean;
  onClick?: () => void;
  rightIcon?: typeof SvgIcon;
}

export interface DashboardNavElementProps {
  sx: SxProps<Theme>;
  name: string;
  path: string;
  outlineIcon: typeof SvgIcon;
  filledIcon: typeof SvgIcon;
  isSelected: boolean;
  onClick: () => void;
  subMenuItems?: DashboardSubMenuItemsProps[];
  bottomFixPosition?: boolean;
  secondaryLabel?: string;
  rightIcon?: typeof SvgIcon;
  onSecondaryLabelClick?: () => void;
}

export function DashboardNavElement(props: DashboardNavElementProps) {
  const theme = useTheme();
  const navigate = useNavigate();

  const NavElementStyle = () => ({
    display: 'block',
    alignItems: 'center',
    color: common.black,
    padding: `${theme.spacing(1.5)} !important`,
    transition: 'all 0.2s',
    '&:hover': {
      color: common.black,
      cursor: 'pointer',
      opacity: 1,
    },
  } as const);

  const NavIconStyle = () => ({
    width: theme.spacing(4.5),
    height: theme.spacing(3.8),
  } as const);

  const NavTypographyStyle = () => ({
    color: common.black,
    marginLeft: theme.spacing(2),
    fontSize: theme.spacing(2),
    lineHeight: '1',
    [theme.breakpoints.down('md')]: {
      marginLeft: theme.spacing(3),
    },
  } as const);
  const {
    sx, path, isSelected,
  } = props;

  const [openSubMenu, setOpenSubMenu] = useState(false);
  const [, setActiveSubMenuIndex] = useState<number | null>(null);
  const location = useLocation(); // Get the current location

  const handleClick = () => {
    setActiveSubMenuIndex(null);
    if (props.subMenuItems?.length) {
      setOpenSubMenu(!openSubMenu);
    } else {
      setOpenSubMenu(false);
      props.onClick();
      if (path !== '#') navigate(path);
    }
  };

  // Open submenu if current page is a submenu item, close otherwise
  useEffect(() => {
    if (props.subMenuItems?.length) {
      const isSubMenuItemCurrent = props.subMenuItems.some(
        (item) => item.isCurrent || location.pathname === item.path,
      );
      setOpenSubMenu(isSubMenuItemCurrent);
    } else if (!location.pathname.includes(path)) {
      setOpenSubMenu(false);
    }
  }, [location.pathname, path, props.subMenuItems]); // Dependency on location.pathname

  const handleSubMenuClick =
    (option: DashboardSubMenuItemsProps, e: React.MouseEvent<HTMLDivElement>, index: number) => {
      if (e && e.stopPropagation) e.stopPropagation();
      setActiveSubMenuIndex(index);
      if (props.onClick) props.onClick();
      if (option.onClick) {
        option.onClick();
      } else {
        navigate(option.path);
      }
    };

  const Icon = () => {
    if (props.subMenuItems?.length) {
      return (openSubMenu ? <KeyboardArrowUpIcon sx={[NavIconStyle]} /> :
        <KeyboardArrowRightIcon sx={[NavIconStyle]} />);
    }
    return (isSelected ?
      <props.filledIcon sx={[NavIconStyle]} /> :
      <props.outlineIcon sx={[NavIconStyle]} />);
  };

  const sanitizeId = (id: string) => id.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  const isSecondaryLabelClickable = Boolean(props.onSecondaryLabelClick);

  return (
    <Box id={`${sanitizeId(props.name)}-menu-item`} data-testid={`${sanitizeId(props.name)}-menu-item`} sx={{ display: 'flex', flexDirection: 'column' }}>
      {!!props.subMenuItems?.length && props.bottomFixPosition &&
        <Collapse in={openSubMenu} timeout="auto" unmountOnExit>
          <List component="div" disablePadding
            sx={{
              mb: theme.spacing(0),
            }}>
            <List component="div" disablePadding>
              {props.subMenuItems?.map((option, index) => (
                <Box
                  sx={{
                    color: theme.palette.common.black,
                    fontWeight: 'bold',
                    display: 'flex',
                    padding: `${theme.spacing(0.75)} ${theme.spacing(1.5)}`,
                    paddingLeft: theme.spacing(8),
                    opacity: option.isCurrent ? 1 : 0.5,
                    backgroundColor: option.isCurrent ? theme.palette.grey[100] : 'transparent',
                    borderRadius: option.isCurrent ? theme.spacing(1.5) : 0,
                    transition: 'all 0.2s',
                    gap: theme.spacing(2),
                    [theme.breakpoints.down('md')]: {
                      gap: theme.spacing(3),
                    },
                    '&:hover': {
                      opacity: 1,
                    },
                  }}
                  key={option.title}
                >
                  <ListItemButton
                    key={index}
                    sx={{
                      padding: 0,
                      '&:hover': {
                        padding: 0,
                        background: 'transparent',
                      },
                    }}
                    onClick={(e) => handleSubMenuClick(option, e, index)}
                  >
                    <ListItemText
                      sx={{
                        color: theme.palette.common.black,
                        alignSelf: 'flex-start',
                        marginTop: 0,
                        marginBottom: 0,
                      }}
                      primary={option.title}
                    />
                    {option.rightIcon && (
                      <option.rightIcon sx={{
                        width: theme.spacing(2),
                        height: theme.spacing(2),
                        opacity: 0.5,
                      }} />
                    )}
                  </ListItemButton>
                </Box>
              ))}
            </List>
          </List>
        </Collapse>}
      <Link style={{ textDecoration: 'none', width: '100%' }}>
        <Box
          sx={[
            NavElementStyle,
            openSubMenu ? {
              color: common.black,
              flexFlow: 'wrap',
              [theme.breakpoints.down('md')]: {
                display: 'block',
              },
            } : {},
            props.isSelected ? {
              backgroundColor: theme.palette.grey[100],
              color: common.black,
              borderRadius: theme.spacing(1.5),
            } : {},
            ...(Array.isArray(sx) ? sx : [sx]),
          ]}
          onClick={handleClick}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              gap: theme.spacing(2),
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                minWidth: 0,
                flexShrink: 1,
                opacity: props.isSelected ? 1 : 0.5,
              }}
            >
              <Icon />
              <Typography variant='h5' sx={NavTypographyStyle()}>{props.name}</Typography>
            </Box>
            {props.secondaryLabel &&
              <Typography
                component={isSecondaryLabelClickable ? 'button' : 'span'}
                variant='labelMd'
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: theme.palette.brand.surface,
                  backgroundColor: theme.palette.brand.primary,
                  borderRadius: theme.spacing(1.25),
                  whiteSpace: 'nowrap',
                  padding: `${theme.spacing(0.1)} ${theme.spacing(0.75)}`,
                  flexShrink: 0,
                  border: isSecondaryLabelClickable ? 'none' : undefined,
                  cursor: isSecondaryLabelClickable ? 'pointer' : 'default',
                  opacity: 1,
                  backgroundSize: '100% 100%',
                  backgroundRepeat: 'no-repeat',
                  transition: 'background-image 0.15s ease',
                  '&:hover': isSecondaryLabelClickable ? {
                    backgroundImage: `linear-gradient(${theme.palette.action.hover}, ${theme.palette.action.hover})`,
                  } : {},
                }}
                data-testid={`${sanitizeId(props.name)}-menu-item-secondary`}
                onClick={(e: React.MouseEvent) => {
                  if (isSecondaryLabelClickable && props.onSecondaryLabelClick) {
                    e.stopPropagation();
                    props.onSecondaryLabelClick();
                  }
                }}
              >
                {props.secondaryLabel}
              </Typography>}
            {props.rightIcon &&
              <props.rightIcon sx={{
                width: theme.spacing(2.5),
                height: theme.spacing(2.5),
                opacity: props.isSelected ? 1 : 0.5,
              }} />}
          </Box>
          {!!props.subMenuItems?.length && !props.bottomFixPosition &&
            <Collapse in={openSubMenu} timeout="auto" unmountOnExit>
              <List component="div" disablePadding
                sx={{
                  mt: theme.spacing(0),
                }}>
                <List component="div" disablePadding>
                  {props.subMenuItems?.map((option, index) => (
                    <Box
                      sx={{
                        color: common.black,
                        fontWeight: 'bold',
                        display: 'flex',
                        padding: `${theme.spacing(0.75)} ${theme.spacing(1.5)}`,
                        paddingLeft: theme.spacing(8),
                        opacity: option.isCurrent ? 1 : 0.5,
                        backgroundColor: option.isCurrent ? theme.palette.grey[100] : 'transparent',
                        borderRadius: option.isCurrent ? theme.spacing(1.5) : 0,
                        transition: 'all 0.2s',
                        gap: theme.spacing(2),
                        [theme.breakpoints.down('md')]: {
                          gap: theme.spacing(3),
                        },
                        '&:hover': {
                          opacity: 1,
                        },
                      }}
                      key={option.title}
                    >
                      <ListItemButton
                        key={index}
                        sx={{
                          padding: 0,
                          '&:hover': {
                            padding: 0,
                            background: 'transparent',
                          },
                        }}
                        onClick={(e) => handleSubMenuClick(option, e, index)}
                      >
                        <ListItemText
                          sx={{
                            color: common.black,
                            alignSelf: 'flex-start',
                            marginTop: 0,
                            marginBottom: 0,
                          }}
                          primary={option.title}
                        />
                        {option.rightIcon && (
                          <option.rightIcon sx={{
                            width: theme.spacing(2),
                            height: theme.spacing(2),
                            opacity: 0.5,
                          }} />
                        )}
                      </ListItemButton>
                    </Box>
                  ))}
                </List>
              </List>
            </Collapse>}
        </Box>
      </Link>
    </Box>
  );
}
